import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Menu,
  ListItemIcon,
  ListItemText,
  Select
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Visibility,
  ExpandMore,
  ExpandLess,
  FilterList,
  Business,
  Security,
  Assessment,
  CloudDone,
  Info as InfoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Apps as AppsIcon,
  Timeline as TimelineIcon,
  MoreVert
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { HoverTableRow } from '../../ssh/SshStyles';
import {
  StyledFormControl,
  StyledSelect
} from "../../fui/FuiStyles";
import base from '../../../config';
import { headers } from '../../../helper/api';
import WorkflowTracker from './WorkflowTracker';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    color: '#6a1b9a', // Purple for section titles
    fontWeight: 'bold',
  },
  statusChip: {
    fontWeight: 'normal',
  },
  // Form Status Colors
  submittedStatus: {
    backgroundColor: '#4caf50', // Green for submitted/success
    color: 'white',
  },
  draftStatus: {
    backgroundColor: '#9e9e9e', // Gray for draft
    color: 'white',
  },
  pendingFormStatus: {
    backgroundColor: '#ff9800', // Orange for pending form submission
    color: 'white',
  },
  // Integration Status Colors
  pendingStatus: {
    backgroundColor: '#ff9800', // Orange for pending review
    color: 'white',
  },
  inProgressStatus: {
    backgroundColor: '#2196f3', // Blue for in progress
    color: 'white',
  },
  testingStatus: {
    backgroundColor: '#9c27b0', // Purple for testing
    color: 'white',
  },
  completedStatus: {
    backgroundColor: '#4caf50', // Green for completed
    color: 'white',
  },
  failedStatus: {
    backgroundColor: '#f44336', // Red for failed
    color: 'white',
  },
  rejectedStatus: {
    backgroundColor: '#e91e63', // Pink for rejected
    color: 'white',
  },
  onHoldStatus: {
    backgroundColor: '#ff5722', // Deep orange for on hold
    color: 'white',
  },
  // Environment Colors
  prodEnvironment: {
    backgroundColor: '#f44336', // Red for production
    color: 'white',
  },
  uatEnvironment: {
    backgroundColor: '#ff9800', // Orange for UAT
    color: 'white',
  },
  devEnvironment: {
    backgroundColor: '#4caf50', // Green for development
    color: 'white',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 500,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderLeft: `4px solid #fdcb6e`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(1),
    border: '1px solid #e9ecef',
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(1.5),
    backgroundColor: 'white',
    borderRadius: theme.spacing(0.5),
    border: '1px solid #dee2e6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  detailsCard: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: 'white',
    border: '1px solid #e3f2fd',
    borderRadius: theme.spacing(1),
  },
});

const SSORequestsList = ({ classes }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    environment: '',
    status: '',
    clientName: '',
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [selectedRequestForApps, setSelectedRequestForApps] = useState(null);
  const [appFormData, setAppFormData] = useState({
    name: '',
    url: '',
    privateDns: '',
    cluster: '',
    clusterRegion: '',
    clusterName: '',
    adGroups: [{
      adGroupType: 'CLIENT',
      groups: '',
      role: 'User',
      application: ''
    }]
  });
  const [editingAppIndex, setEditingAppIndex] = useState(-1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAppForm, setShowAppForm] = useState(false);
  const [newAdGroup, setNewAdGroup] = useState('');
  const [adGroupType, setAdGroupType] = useState('client');
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [selectedRequestForWorkflow, setSelectedRequestForWorkflow] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRequestForAction, setSelectedRequestForAction] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    testing: 0,
    completed: 0
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const idToken = localStorage.getItem('token');
      const response = await fetch(`${base.app.apiSite}sso/list`, {
        method: 'GET',
        headers: Object.assign({}, headers, {
          Authorization: idToken
        })
      });

      if (response.ok) {
        const result = await response.json();
        const requestsList = result.data || [];
        setRequests(requestsList);

        // Calculate stats for integration status
        const statsData = {
          total: requestsList.length,
          pending: requestsList.filter(r => r.IntegrationStatus === 'PENDING_REVIEW' || !r.IntegrationStatus).length,
          inProgress: requestsList.filter(r => r.IntegrationStatus === 'IN_PROGRESS').length,
          testing: requestsList.filter(r => r.IntegrationStatus === 'TESTING').length,
          completed: requestsList.filter(r => r.IntegrationStatus === 'COMPLETED').length
        };
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (requestId) => {
    try {
      const idToken = localStorage.getItem('token');
      const response = await fetch(`${base.app.apiSite}sso/get?requestId=${requestId}`, {
        method: 'GET',
        headers: Object.assign({}, headers, {
          Authorization: idToken
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSelectedRequest(result.data);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
    }
  };

  const getFormStatusClass = (status) => {
    switch (status && status.toUpperCase()) {
      case 'SUBMITTED': return classes.submittedStatus;
      case 'DRAFT': return classes.draftStatus;
      case 'PENDING': return classes.pendingFormStatus;
      case 'INCOMPLETE': return classes.draftStatus;
      default: return classes.submittedStatus;
    }
  };

  const getFormStatusLabel = (status) => {
    switch (status) {
      case 'SUBMITTED': return 'Submitted';
      case 'DRAFT': return 'Draft';
      default: return 'Submitted';
    }
  };

  const getIntegrationStatusClass = (status) => {
    switch (status) {
      case 'PENDING_REVIEW': return classes.pendingStatus;
      case 'IN_PROGRESS': return classes.inProgressStatus;
      case 'TESTING': return classes.testingStatus;
      case 'COMPLETED': return classes.completedStatus;
      case 'FAILED': return classes.failedStatus;
      case 'REJECTED': return classes.rejectedStatus;
      case 'ON_HOLD': return classes.onHoldStatus;
      default: return classes.pendingStatus;
    }
  };

  const getIntegrationStatusLabel = (status) => {
    switch (status) {
      case 'PENDING_REVIEW': return 'Pending Review';
      case 'IN_PROGRESS': return 'In Progress';
      case 'TESTING': return 'Testing';
      case 'COMPLETED': return 'Completed';
      case 'FAILED': return 'Failed';
      case 'REJECTED': return 'Rejected';
      case 'ON_HOLD': return 'On Hold';
      default: return 'Pending Review';
    }
  };

  const getEnvironmentClass = (environment) => {
    switch (environment) {
      case 'Prod': return classes.prodEnvironment;
      case 'UAT': return classes.uatEnvironment;
      case 'Dev': return classes.devEnvironment;
      default: return classes.devEnvironment;
    }
  };

  const getEnvironmentStyle = (environment) => {
    switch (environment) {
      case 'Prod': return { backgroundColor: '#f44336', color: 'white' }; // Red for production
      case 'UAT': return { backgroundColor: '#ff9800', color: 'white' }; // Orange for UAT
      case 'Dev': return { backgroundColor: '#4caf50', color: 'white' }; // Green for development
      default: return { backgroundColor: '#9e9e9e', color: 'white' }; // Gray for unknown
    }
  };

  const getIntegrationStatusStyle = (status) => {
    switch (status) {
      case 'PENDING_REVIEW': return { backgroundColor: '#ff9800', color: 'white' }; // Orange
      case 'IN_PROGRESS': return { backgroundColor: '#2196f3', color: 'white' }; // Blue
      case 'TESTING': return { backgroundColor: '#9c27b0', color: 'white' }; // Purple
      case 'COMPLETED': return { backgroundColor: '#4caf50', color: 'white' }; // Green
      case 'FAILED': return { backgroundColor: '#f44336', color: 'white' }; // Red
      case 'REJECTED': return { backgroundColor: '#e91e63', color: 'white' }; // Pink
      case 'ON_HOLD': return { backgroundColor: '#ff5722', color: 'white' }; // Deep orange
      default: return { backgroundColor: '#9e9e9e', color: 'white' }; // Gray for unknown
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowExpand = (requestId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const handleManageApplications = (request) => {
    setSelectedRequestForApps(request);
    setAppDialogOpen(true);
    setShowAppForm(false); // Always start with list view
    // Reset form state
    setIsEditMode(false);
    setEditingAppIndex(-1);
    setNewAdGroup('');
    setAdGroupType('client');
  };

  const handleViewWorkflow = (request) => {
    setSelectedRequestForWorkflow(request);
    setWorkflowDialogOpen(true);
  };

  const handleActionMenuOpen = (event, request) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedRequestForAction(request);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedRequestForAction(null);
  };

  const handleMenuAction = (action) => {
    if (!selectedRequestForAction) return;

    switch (action) {
      case 'view-details':
        handleViewDetails(selectedRequestForAction.RequestID);
        break;
      case 'expand':
        handleRowExpand(selectedRequestForAction.RequestID);
        break;
      case 'manage-apps':
        handleManageApplications(selectedRequestForAction);
        break;
      case 'view-workflow':
        handleViewWorkflow(selectedRequestForAction);
        break;
    }
    handleActionMenuClose();
  };

  const handleAddApplication = () => {
    setIsEditMode(false);
    setEditingAppIndex(-1);
    setShowAppForm(true);
    setAppFormData({
      name: '',
      url: '',
      privateDns: '',
      cluster: '',
      clusterRegion: '',
      clusterName: '',
      adGroups: [{
        adGroupType: 'CLIENT',
        groups: '',
        role: 'User',
        application: ''
      }]
    });
  };

  const handleEditApplication = (appIndex, app) => {
    setIsEditMode(true);
    setEditingAppIndex(appIndex);
    setShowAppForm(true);

    // Handle different adGroups formats - existing apps might have simple array format
    let formattedAdGroups;
    if (app.adGroups && Array.isArray(app.adGroups)) {
      // Check if it's the new format (array of objects) or old format (array of strings)
      if (app.adGroups.length > 0 && typeof app.adGroups[0] === 'object') {
        // New format - use as is
        formattedAdGroups = app.adGroups;
      } else {
        // Old format - convert strings to new format
        formattedAdGroups = app.adGroups.length > 0 ? app.adGroups.map(group => ({
          adGroupType: 'CLIENT',
          groups: group,
          role: 'User',
          application: app.name || ''
        })) : [{
          adGroupType: 'CLIENT',
          groups: '',
          role: 'User',
          application: app.name || ''
        }];
      }
    } else {
      // No adGroups - create default entry
      formattedAdGroups = [{
        adGroupType: 'CLIENT',
        groups: '',
        role: 'User',
        application: app.name || ''
      }];
    }

    setAppFormData({
      name: app.name || '',
      url: app.url || app.applicationUrl || '',
      privateDns: app.privateDns || '',
      cluster: app.cluster || '',
      clusterRegion: app.clusterRegion || '',
      clusterName: app.clusterName || '',
      adGroups: formattedAdGroups
    });
  };

  const handleAppFormChange = (field, value) => {
    setAppFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // If name changes, update all adGroups applications
      if (field === 'name' && updated.adGroups && Array.isArray(updated.adGroups)) {
        updated.adGroups = updated.adGroups.map(group => ({
          ...group,
          application: value || ''
        }));
      }

      return updated;
    });
  };

  const handleAddAdGroup = () => {
    const group = newAdGroup.trim();
    if (!group) return;

    // Check if group already exists (handle both old and new format)
    const groupExists = appFormData.adGroups.some(existingGroup => {
      if (typeof existingGroup === 'string') {
        return existingGroup === group;
      } else {
        return existingGroup.groups === group;
      }
    });

    if (groupExists) return;

    // Add as new format object with selected type
    const newGroupEntry = {
      adGroupType: adGroupType.toUpperCase(),
      groups: group,
      role: 'User',
      application: appFormData.name || ''
    };

    setAppFormData(prev => ({
      ...prev,
      adGroups: [...prev.adGroups, newGroupEntry]
    }));
    setNewAdGroup('');
  };

  const handleAddExistingAdGroup = (group) => {
    if (!group) return;

    // Check if group already exists (handle both old and new format)
    const groupExists = appFormData.adGroups.some(existingGroup => {
      if (typeof existingGroup === 'string') {
        return existingGroup === group;
      } else {
        return existingGroup.groups === group;
      }
    });

    if (groupExists) return;

    // Determine if it's a client or accenture group from SSO request
    let groupType = 'CLIENT'; // default
    if (selectedRequestForApps && selectedRequestForApps.FullRequestData) {
      const fullData = typeof selectedRequestForApps.FullRequestData === 'string'
        ? JSON.parse(selectedRequestForApps.FullRequestData)
        : selectedRequestForApps.FullRequestData;

      if (fullData.accessControl && fullData.accessControl.roleMappings) {
        const allAccentureGroups = [];
        fullData.accessControl.roleMappings.forEach(mapping => {
          if (mapping.accentureAdGroups) {
            allAccentureGroups.push(...mapping.accentureAdGroups);
          }
        });

        if (allAccentureGroups.includes(group)) {
          groupType = 'ACCENTURE';
        }
      }
    }

    // Add as new format object
    const newGroupEntry = {
      adGroupType: groupType,
      groups: group,
      role: 'User',
      application: appFormData.name || ''
    };

    setAppFormData(prev => ({
      ...prev,
      adGroups: [...prev.adGroups, newGroupEntry]
    }));
  };

  const handleAddAdGroupEntry = () => {
    setAppFormData(prev => ({
      ...prev,
      adGroups: [...prev.adGroups, {
        adGroupType: 'CLIENT',
        groups: '',
        role: 'User',
        application: prev.name || ''
      }]
    }));
  };

  const handleRemoveAdGroupEntry = (index) => {
    setAppFormData(prev => ({
      ...prev,
      adGroups: prev.adGroups.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateAdGroup = (index, field, value) => {
    setAppFormData(prev => ({
      ...prev,
      adGroups: prev.adGroups.map((group, i) =>
        i === index ? { ...group, [field]: value } : group
      )
    }));
  };

  const handleRemoveAdGroup = (index) => {
    setAppFormData(prev => ({
      ...prev,
      adGroups: prev.adGroups.filter((_, i) => i !== index)
    }));
  };

  const handleCloseAppDialog = () => {
    setAppDialogOpen(false);
    setShowAppForm(false);
    setNewAdGroup('');
    setAdGroupType('client');
    setIsEditMode(false);
    setEditingAppIndex(-1);
    // Reset form data
    setAppFormData({
      name: '',
      url: '',
      privateDns: '',
      cluster: '',
      clusterRegion: '',
      clusterName: '',
      adGroups: [{
        adGroupType: 'CLIENT',
        groups: '',
        role: 'User',
        application: ''
      }]
    });
  };

  const handleManageApps = (request) => {
    setSelectedRequestForApps(request);
    setAppDialogOpen(true);
    setShowAppForm(false); // Always start with list view
  };

  const handleBackToList = () => {
    setShowAppForm(false);
    setIsEditMode(false);
    setEditingAppIndex(-1);
    setNewAdGroup('');
    setAdGroupType('client');
    // Reset form data
    setAppFormData({
      name: '',
      url: '',
      privateDns: '',
      cluster: '',
      clusterRegion: '',
      clusterName: '',
      adGroups: [{
        adGroupType: 'CLIENT',
        groups: '',
        role: 'User',
        application: ''
      }]
    });
  };

  const handleSaveApplication = async () => {
    if (!selectedRequestForApps || !appFormData.name.trim() || !appFormData.url.trim()) {
      return; // Basic validation
    }

    try {
      const idToken = localStorage.getItem('token');
      const newApp = {
        name: appFormData.name.trim(),
        url: appFormData.url.trim(),
        adGroups: appFormData.adGroups || [],
        roleMappings: appFormData.adGroups || [],
        hostingDetails: {
          type: selectedRequestForApps.HostingType || 'Shared',
          environment: selectedRequestForApps.Environment || 'Dev'
        }
      };

      // Add hosting-specific fields based on hosting type
      if (selectedRequestForApps.HostingType === 'Dedicated') {
        newApp.privateDns = appFormData.privateDns.trim() || null;
      } else {
        // Shared hosting
        newApp.cluster = appFormData.cluster.trim() || null;
        newApp.clusterRegion = appFormData.clusterRegion.trim() || null;
        newApp.clusterName = appFormData.clusterName.trim() || null;
      }

      // Get current user information for tracking
      let currentUserId = 'admin';
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload_token = parts[1];
            const padded = payload_token + '='.repeat((4 - payload_token.length % 4) % 4);
            const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
            const claims = JSON.parse(decoded);
            currentUserId = claims.email || claims.preferred_username || claims.upn || 'admin';
          }
        }
      } catch (error) {
        // console.log('Could not extract user from token, using admin');
      }

      // Determine the action based on edit mode
      const action = isEditMode ? 'update' : 'add';
      const payload = {
        requestId: selectedRequestForApps.RequestID,
        application: newApp,
        action: action,
        userId: currentUserId
      };

      // Add app index for update operations
      if (isEditMode) {
        payload.appIndex = editingAppIndex;
      }

      // Update the request with the application
      const response = await fetch(`${base.app.apiSite}sso/update-applications`, {
        method: 'POST',
        headers: Object.assign({}, headers, {
          Authorization: idToken,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Refresh the requests list
        fetchRequests();
        // Go back to list view instead of closing dialog
        handleBackToList();
      }
    } catch (error) {
      console.error('Error adding application:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredRequests = requests.filter(request => {
    return (
      (filters.environment === '' || request.Environment === filters.environment) &&
      (filters.status === '' || (request.IntegrationStatus || 'PENDING_REVIEW') === filters.status) &&
      (filters.clientName === '' || (request.ClientName && request.ClientName.toLowerCase().includes(filters.clientName.toLowerCase())))
    );
  });

  const displayedRequests = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Typography variant="h4" className={classes.sectionTitle}>
          Client SSO Integration Requests Management
        </Typography>
        <Box>
          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
            style={{ borderColor: '#6a1b9a', color: '#6a1b9a', marginRight: 16 }}
          >
            Filters
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchRequests}
            variant="contained"
            disabled={loading}
            style={{ backgroundColor: '#6a1b9a', color: 'white' }}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      {/* Statistics Overview */}
      <Box className={classes.statsContainer}>
        <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
          Integration Status Overview
        </Typography>
        <Box style={{ display: 'flex', gap: 16, width: '100%' }}>
          <Box className={classes.statCard} style={{ flex: 1 }}>
            <Typography variant="h4" style={{ color: '#6a1b9a', fontWeight: 'bold' }}>
              {stats.total}
            </Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>
              Total Requests
            </Typography>
          </Box>
          <Box className={classes.statCard} style={{ flex: 1 }}>
            <Typography variant="h4" style={{ color: '#ff9800', fontWeight: 'bold' }}>
              {stats.pending}
            </Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>
              Pending Review
            </Typography>
          </Box>
          <Box className={classes.statCard} style={{ flex: 1 }}>
            <Typography variant="h4" style={{ color: '#2196f3', fontWeight: 'bold' }}>
              {stats.inProgress}
            </Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>
              In Progress
            </Typography>
          </Box>
          <Box className={classes.statCard} style={{ flex: 1 }}>
            <Typography variant="h4" style={{ color: '#9c27b0', fontWeight: 'bold' }}>
              {stats.testing}
            </Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>
              Testing
            </Typography>
          </Box>
          <Box className={classes.statCard} style={{ flex: 1 }}>
            <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>
              {stats.completed}
            </Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>
              Completed
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters Section */}
      <Collapse in={showFilters}>
        <Box style={{
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #e9ecef'
        }}>
          <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
            Filter Options
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Client Name"
                value={filters.clientName}
                onChange={(e) => setFilters(Object.assign({}, filters, { clientName: e.target.value }))}
                variant="outlined"
                size="small"
                placeholder="Enter client name to search..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  value={filters.environment}
                  onChange={(e) => setFilters(Object.assign({}, filters, { environment: e.target.value }))}
                  label="Environment"
                >
                  <MenuItem value="">All Environments</MenuItem>
                  <MenuItem value="Dev">Development</MenuItem>
                  <MenuItem value="UAT">UAT</MenuItem>
                  <MenuItem value="Prod">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Integration Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(Object.assign({}, filters, { status: e.target.value }))}
                  label="Integration Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="PENDING_REVIEW">Pending Review</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="TESTING">Testing</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Requests Table */}
      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: '#fff',
        marginBottom: '16px'
      }}>
        <TableContainer className={classes.tableContainer} style={{ paddingTop: 0, marginTop: 0 }}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : filteredRequests.length === 0 ? (
          <Box style={{
            textAlign: 'center',
            padding: '48px 32px',
            color: '#666',
            backgroundColor: '#f8f9fa'
          }}>
            <InfoIcon style={{ fontSize: 48, marginBottom: 16, color: '#ccc' }} />
            <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
              No SSO Integration Requests Found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {Object.values(filters).some(f => f !== '') ?
                'Try adjusting your filters to see more results.' :
                'No requests have been submitted yet.'
              }
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Request ID</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Client Name</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Environment</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Technical Contact</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>IdP Provider</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Integration Status</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Workflow Progress</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Submitted</TableCell>
                <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRequests.map((request, index) => (
                <React.Fragment key={request.RequestID}>
                  <HoverTableRow>
                    <TableCell style={{ width: '140px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                      {request.RequestID}
                    </TableCell>
                    <TableCell style={{ maxWidth: '150px' }}>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {request.ClientName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.Environment || 'N/A'}
                        size="small"
                        className={`${classes.statusChip}`}
                        style={{
                          fontSize: '0.7rem',
                          ...getEnvironmentStyle(request.Environment)
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ maxWidth: '180px' }}>
                      <Typography variant="body2" style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {request.TechnicalContact || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.IdPProvider || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getIntegrationStatusLabel(request.IntegrationStatus)}
                        size="small"
                        className={`${classes.statusChip}`}
                        style={{
                          ...getIntegrationStatusStyle(request.IntegrationStatus)
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: '0.8rem', minWidth: '120px' }}>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        Step {request.CurrentStep || 1}/8
                      </Typography>
                      <Typography variant="caption" style={{ color: '#6a1b9a' }}>
                        {request.WorkflowStatus ?
                          request.WorkflowStatus.replace(/STEP_\d+_/, '').replace(/_/g, ' ') :
                          'PENDING'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell style={{ fontSize: '0.8rem' }}>
                      {formatDate(request.Timestamp)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowExpand(request.RequestID)}
                        style={{ color: '#6a1b9a', marginRight: 4 }}
                        title="Expand/Collapse Details"
                      >
                        {expandedRows.has(request.RequestID) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(event) => handleActionMenuOpen(event, request)}
                        style={{
                          color: '#6a1b9a',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}
                        title="More Actions"
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </HoverTableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={9}>
                      <Collapse in={expandedRows.has(request.RequestID)} timeout="auto" unmountOnExit>
                        <Box style={{
                          margin: '8px 0',
                          padding: '16px 20px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e3f2fd',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <Typography variant="subtitle2" style={{
                            fontWeight: 600,
                            marginBottom: 12,
                            color: '#6a1b9a'
                          }}>
                            Quick Overview
                          </Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Box style={{ marginBottom: '8px' }}>
                                <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>
                                  CLIENT CODE
                                </Typography>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {request.ClientCode || 'Not specified'}
                                </Typography>
                              </Box>
                              <Box style={{ marginBottom: '8px' }}>
                                <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>
                                  HOSTING TYPE
                                </Typography>
                                <Typography variant="body2">
                                  {request.HostingType || 'Not specified'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box style={{ marginBottom: '8px' }}>
                                <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>
                                  PROTOCOL
                                </Typography>
                                <Typography variant="body2">
                                  {request.Protocol || 'Not specified'}
                                </Typography>
                              </Box>
                              <Box style={{ marginBottom: '8px' }}>
                                <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>
                                  APPLICATIONS COUNT
                                </Typography>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {(request.FullRequestData && request.FullRequestData.applications && request.FullRequestData.applications.length) || 0} configured
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      </div>

      {/* Pagination */}
      {!loading && filteredRequests.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRequests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}


      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">SSO Integration Request Details</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRawData(!showRawData)}
            >
              {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {selectedRequest && selectedRequest.FullRequestData && (
            <Box>
              <Typography variant="h5" gutterBottom style={{ color: '#1976d2', fontWeight: 600, marginBottom: 24 }}>
                {selectedRequest.ClientName} - {selectedRequest.RequestID}
              </Typography>

              {/* Client Configuration */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <Business style={{ marginRight: 8 }} />
                  Client Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Client Name:</strong> {selectedRequest.FullRequestData.clientConfiguration.clientName || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Client Code:</strong> {selectedRequest.FullRequestData.clientConfiguration.clientCode || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Environment:</strong> {selectedRequest.FullRequestData.clientConfiguration.environment || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Technical Contact:</strong> {selectedRequest.FullRequestData.clientConfiguration.technicalContact || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Hosting Type:</strong> {selectedRequest.FullRequestData.clientConfiguration.hostingType || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Identity Provider */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <Security style={{ marginRight: 8 }} />
                  Identity Provider Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Provider:</strong> {selectedRequest.FullRequestData.identityProvider.provider || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Protocol:</strong> {selectedRequest.FullRequestData.identityProvider.protocol || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Tenant ID:</strong> {selectedRequest.FullRequestData.identityProvider.tenantId || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Metadata URL:</strong> {selectedRequest.FullRequestData.identityProvider.metadataUrl || 'Not provided'}</Typography>
                    <Typography variant="body2"><strong>Issuer URL:</strong> {selectedRequest.FullRequestData.identityProvider.issuerUrl || 'Not provided'}</Typography>
                    <Typography variant="body2"><strong>Client ID:</strong> {selectedRequest.FullRequestData.identityProvider.clientId || 'Not provided'}</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Integration Details */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <CloudDone style={{ marginRight: 8 }} />
                  Integration Details
                </Typography>
                <Typography variant="body2"><strong>SP Entity ID:</strong> {selectedRequest.FullRequestData.integrationDetails.spEntityId || 'N/A'}</Typography>
                <Typography variant="body2"><strong>ACS URL:</strong> {selectedRequest.FullRequestData.integrationDetails.acsUrl || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Single Logout URL:</strong> {selectedRequest.FullRequestData.integrationDetails.singleLogoutUrl || 'N/A'}</Typography>
              </Card>

              {/* Applications */}
              {selectedRequest.FullRequestData.applications && selectedRequest.FullRequestData.applications.length > 0 && (
                <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2' }}>
                    Applications ({selectedRequest.FullRequestData.applications.length})
                  </Typography>
                  {selectedRequest.FullRequestData.applications.map((app, index) => (
                    <Box key={index} style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                        {index + 1}. {app.name}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2"><strong>Application URL:</strong> {app.url || app.applicationUrl || 'Not provided'}</Typography>
                          <Typography variant="body2"><strong>Private DNS:</strong> {app.privateDns || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2"><strong>Cluster:</strong> {app.cluster || 'N/A'}</Typography>
                          <Typography variant="body2"><strong>Hosting Details:</strong> {app.hostingDetails && typeof app.hostingDetails === 'object' ? JSON.stringify(app.hostingDetails) : (app.hostingDetails || 'N/A')}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Card>
              )}

              {/* Access Control */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <Assessment style={{ marginRight: 8 }} />
                  Access Control & Claims
                </Typography>

                {/* Role Mappings */}
                {selectedRequest.FullRequestData.accessControl && selectedRequest.FullRequestData.accessControl.roleMappings && selectedRequest.FullRequestData.accessControl.roleMappings.length > 0 && (
                  <Box style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                      Role Mappings ({selectedRequest.FullRequestData.accessControl.roleMappings.length})
                    </Typography>
                    {selectedRequest.FullRequestData.accessControl.roleMappings.map((mapping, index) => (
                      <Box key={index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#f0f8ff', borderRadius: 4 }}>
                        <Typography variant="body2"><strong>Application:</strong> {mapping.application}</Typography>
                        <Typography variant="body2"><strong>Role:</strong> {mapping.role}</Typography>
                        {/* Smart categorization for both new and legacy formats */}
                        {(() => {
                          // For new format with explicit categorization
                          if (mapping.adGroupType) {
                            return (
                              <>
                                <Typography variant="body2">
                                  <strong>AD Group Type:</strong> {mapping.adGroupType === 'ACCENTURE' ? 'Accenture AD Groups' : 'Client AD Groups'}
                                </Typography>
                                {mapping.adGroupType === 'ACCENTURE' && mapping.accentureAdGroups && mapping.accentureAdGroups.length > 0 && (
                                  <Typography variant="body2"><strong>Accenture AD Groups:</strong> {mapping.accentureAdGroups.join(', ')}</Typography>
                                )}
                                {mapping.adGroupType === 'CLIENT' && mapping.clientAdGroups && mapping.clientAdGroups.length > 0 && (
                                  <Typography variant="body2"><strong>Client AD Groups:</strong> {mapping.clientAdGroups.join(', ')}</Typography>
                                )}
                              </>
                            );
                          }

                          // For legacy format - intelligently categorize by naming convention
                          if (mapping.adGroups && mapping.adGroups.length > 0) {
                            const accentureGroups = mapping.adGroups.filter(group =>
                              group && (group.toLowerCase().includes('acn') ||
                                       group.toLowerCase().includes('accenture') ||
                                       group.toLowerCase().startsWith('ace_') ||
                                       group.toLowerCase().startsWith('acn_'))
                            );
                            const clientGroups = mapping.adGroups.filter(group =>
                              group && !(group.toLowerCase().includes('acn') ||
                                        group.toLowerCase().includes('accenture') ||
                                        group.toLowerCase().startsWith('ace_') ||
                                        group.toLowerCase().startsWith('acn_'))
                            );

                            return (
                              <>
                                {accentureGroups.length > 0 && (
                                  <Typography variant="body2"><strong>Accenture AD Groups:</strong> {accentureGroups.join(', ')}</Typography>
                                )}
                                {clientGroups.length > 0 && (
                                  <Typography variant="body2"><strong>Client AD Groups:</strong> {clientGroups.join(', ')}</Typography>
                                )}
                              </>
                            );
                          }

                          return null;
                        })()}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Claim Mappings */}
                {selectedRequest.FullRequestData.accessControl && selectedRequest.FullRequestData.accessControl.claimMappings && (
                  <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                      Claim Mappings
                    </Typography>
                    <Box style={{ padding: 8, backgroundColor: '#f0f8ff', borderRadius: 4 }}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedRequest.FullRequestData.accessControl.claimMappings.email || 'Not mapped'}</Typography>
                      <Typography variant="body2"><strong>Given Name:</strong> {selectedRequest.FullRequestData.accessControl.claimMappings.givenName || 'Not mapped'}</Typography>
                      <Typography variant="body2"><strong>Family Name:</strong> {selectedRequest.FullRequestData.accessControl.claimMappings.familyName || 'Not mapped'}</Typography>
                      <Typography variant="body2"><strong>Groups:</strong> {selectedRequest.FullRequestData.accessControl.claimMappings.groups || 'Not mapped'}</Typography>
                    </Box>
                  </Box>
                )}
              </Card>

              {/* Test Users */}
              {selectedRequest.FullRequestData.testUsers && selectedRequest.FullRequestData.testUsers.length > 0 && (
                <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2' }}>
                    Test Users ({selectedRequest.FullRequestData.testUsers.length})
                  </Typography>
                  {selectedRequest.FullRequestData.testUsers.map((user, index) => (
                    <Box key={index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#fff3e0', borderRadius: 4 }}>
                      <Typography variant="body2"><strong>Email:</strong> {user.email}</Typography>
                      <Typography variant="body2"><strong>AD Groups:</strong> {user.adGroups && user.adGroups.length > 0 ? user.adGroups.join(', ') : (user.groups || 'No groups specified')}</Typography>
                    </Box>
                  ))}
                </Card>
              )}

              {/* Request Information */}
              {selectedRequest.FullRequestData.metadata && (
                <Card className={classes.detailsCard}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                    <InfoIcon style={{ marginRight: 8 }} />
                    Request Information
                  </Typography>
                  <Typography variant="body2"><strong>Submitted By:</strong> {selectedRequest.FullRequestData.metadata.submittedBy || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Submission Time:</strong> {selectedRequest.FullRequestData.metadata.submissionTime ? new Date(selectedRequest.FullRequestData.metadata.submissionTime).toLocaleString() : 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Form Version:</strong> {selectedRequest.FullRequestData.metadata.formVersion || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Source:</strong> {selectedRequest.FullRequestData.metadata.source || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Request ID:</strong> {selectedRequest.FullRequestData.requestId || selectedRequest.RequestID}</Typography>
                </Card>
              )}

              {/* Raw Data View */}
              {showRawData && (
                <Card className={classes.detailsCard} style={{ marginTop: 16 }}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2' }}>
                    Raw JSON Data
                  </Typography>
                  <Box style={{
                    backgroundColor: '#f5f5f5',
                    padding: 16,
                    borderRadius: 4,
                    maxHeight: 300,
                    overflow: 'auto'
                  }}>
                    <pre style={{
                      margin: 0,
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>
                      {JSON.stringify(selectedRequest.FullRequestData, null, 2)}
                    </pre>
                  </Box>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Management Dialog */}
      <Dialog
        open={appDialogOpen}
        onClose={handleCloseAppDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" style={{ color: '#6a1b9a' }}>
            {showAppForm
              ? (isEditMode ? 'Edit Application' : 'Add New Application')
              : `Manage Applications - ${selectedRequestForApps && selectedRequestForApps.ClientName}`
            }
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: 0, minHeight: 500 }}>
          {selectedRequestForApps && selectedRequestForApps.FullRequestData && (
            <Box>
              {!showAppForm ? (
                // Applications List View
                <Box style={{ padding: 24 }}>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Typography variant="subtitle1" style={{ color: '#666', fontWeight: 600 }}>
                      Current Applications
                    </Typography>
                    <Button
                      onClick={handleAddApplication}
                      variant="contained"
                      startIcon={<AddIcon />}
                      style={{ backgroundColor: '#6a1b9a', color: 'white' }}
                    >
                      Add Application
                    </Button>
                  </Box>
              {selectedRequestForApps.FullRequestData &&
               selectedRequestForApps.FullRequestData.applications &&
               selectedRequestForApps.FullRequestData.applications.length > 0 ? (
                <Box style={{ marginBottom: 24 }}>
                  {selectedRequestForApps.FullRequestData.applications.map((app, index) => (
                    <Card key={index} style={{
                      marginBottom: 12,
                      padding: 16,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e3f2fd'
                    }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={10}>
                          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#6a1b9a' }}>
                            {app.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            URL: {app.url || app.applicationUrl || 'Not specified'}
                          </Typography>
                          {selectedRequestForApps.HostingType === 'Dedicated' && app.privateDns && (
                            <Typography variant="body2" color="textSecondary">
                              Private DNS: {app.privateDns}
                            </Typography>
                          )}
                          {selectedRequestForApps.HostingType === 'Shared' && app.cluster && (
                            <Typography variant="body2" color="textSecondary">
                              Cluster: {app.cluster} ({app.clusterRegion})
                            </Typography>
                          )}
                          {app.adGroups && Array.isArray(app.adGroups) && app.adGroups.length > 0 && (
                            <Box style={{ marginTop: 8 }}>
                              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                                AD Groups:
                              </Typography>
                              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {app.adGroups.filter(group => group != null).map((group, groupIndex) => {
                                  // Handle both old format (strings) and new format (objects)
                                  let groupLabel;
                                  if (typeof group === 'string') {
                                    groupLabel = group;
                                  } else if (group && group.groups) {
                                    groupLabel = group.groups;
                                  } else {
                                    return null; // Skip invalid entries
                                  }

                                  return (
                                    <Chip
                                      key={groupIndex}
                                      label={groupLabel}
                                      size="small"
                                      style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontSize: '0.7rem' }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            style={{ color: '#6a1b9a' }}
                            onClick={() => handleEditApplication(index, app)}
                            title="Edit Application"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box style={{
                  textAlign: 'center',
                  padding: 32,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 8,
                  marginBottom: 24
                }}>
                  <AppsIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="body1" color="textSecondary">
                    No applications configured yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Add your first application below
                  </Typography>
                </Box>
              )}
                </Box>
              ) : (
                // Application Form View
                <Box style={{ padding: 24 }}>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Typography variant="h6" style={{ color: '#6a1b9a' }}>
                      {isEditMode ? 'Edit Application' : 'Add New Application'}
                    </Typography>
                    <Button
                      onClick={handleBackToList}
                      variant="outlined"
                      style={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
                    >
                      ← Back to Applications
                    </Button>
                  </Box>


              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Application Name"
                    value={appFormData.name}
                    onChange={(e) => handleAppFormChange('name', e.target.value)}
                    variant="outlined"
                    size="small"
                    required
                    placeholder="e.g., MyWizard Portal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Application URL"
                    value={appFormData.url}
                    onChange={(e) => handleAppFormChange('url', e.target.value)}
                    variant="outlined"
                    size="small"
                    required
                    placeholder="https://portal.example.com"
                  />
                </Grid>

                {/* Hosting-specific fields */}
                {selectedRequestForApps.HostingType === 'Dedicated' ? (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Private DNS"
                      value={appFormData.privateDns}
                      onChange={(e) => handleAppFormChange('privateDns', e.target.value)}
                      variant="outlined"
                      size="small"
                      placeholder="ip-172-31-45-123.ap-southeast-1.compute.internal"
                      helperText="AWS internal DNS format (e.g., ip-x-x-x-x.ec2.internal)"
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Cluster"
                        value={appFormData.cluster}
                        onChange={(e) => handleAppFormChange('cluster', e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="prod-cluster-01"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Cluster Region"
                        value={appFormData.clusterRegion}
                        onChange={(e) => handleAppFormChange('clusterRegion', e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="us-east-1"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Cluster Name"
                        value={appFormData.clusterName}
                        onChange={(e) => handleAppFormChange('clusterName', e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="prod-analytics-cluster"
                      />
                    </Grid>
                  </>
                )}

                {/* AD Groups Input - Different for Add vs Edit */}
                {!isEditMode ? (
                  // Replicate exact same AD groups section from initial SSO request
                  <Grid item xs={12}>
                    <Box style={{ marginTop: 16, padding: 16, border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 'bold', color: '#6a1b9a' }}>
                        AD Groups Configuration
                      </Typography>

                      {appFormData.adGroups && Array.isArray(appFormData.adGroups) && appFormData.adGroups.map((group, index) => (
                        <Box key={index} style={{
                          marginBottom: 16,
                          padding: 12,
                          backgroundColor: '#f8f9fa',
                          borderRadius: 6,
                          border: '1px solid #e9ecef'
                        }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>AD Group Type</InputLabel>
                                <Select
                                  value={group.adGroupType || 'CLIENT'}
                                  onChange={(e) => handleUpdateAdGroup(index, 'adGroupType', e.target.value)}
                                  label="AD Group Type"
                                >
                                  <MenuItem value="CLIENT">Client AD Groups</MenuItem>
                                  <MenuItem value="ACCENTURE">Accenture AD Groups</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="AD Groups"
                                value={group.groups || ''}
                                onChange={(e) => handleUpdateAdGroup(index, 'groups', e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Group1, Group2"
                                helperText="Comma separated"
                              />
                            </Grid>

                            <Grid item xs={12} sm={2}>
                              <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Role</InputLabel>
                                <Select
                                  value={group.role || 'User'}
                                  onChange={(e) => handleUpdateAdGroup(index, 'role', e.target.value)}
                                  label="Role"
                                >
                                  <MenuItem value="User">User</MenuItem>
                                  <MenuItem value="Admin">Admin</MenuItem>
                                  <MenuItem value="Viewer">Viewer</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Application"
                                value={group.application || appFormData.name}
                                onChange={(e) => handleUpdateAdGroup(index, 'application', e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Application Name"
                              />
                            </Grid>

                            <Grid item xs={12} sm={1}>
                              <IconButton
                                onClick={() => handleRemoveAdGroupEntry(index)}
                                size="small"
                                style={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}

                      <Button
                        onClick={handleAddAdGroupEntry}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        style={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
                      >
                        Add AD Group Mapping
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  // Full AD Groups Management Section for editing existing applications
                  <Grid item xs={12}>
                    <Box style={{ marginTop: 24, padding: 16, border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      <Typography variant="subtitle1" style={{ color: '#6a1b9a', marginBottom: 16, fontWeight: 'bold' }}>
                        AD Groups Management
                      </Typography>

                      {/* Show existing configured AD Groups from request */}
                      {selectedRequestForApps && selectedRequestForApps.FullRequestData && (() => {
                        const fullData = typeof selectedRequestForApps.FullRequestData === 'string'
                          ? JSON.parse(selectedRequestForApps.FullRequestData)
                          : selectedRequestForApps.FullRequestData;

                        // Extract AD groups from roleMappings
                        const allClientGroups = [];
                        const allAccentureGroups = [];

                        if (fullData.accessControl && fullData.accessControl.roleMappings) {
                          fullData.accessControl.roleMappings.forEach(mapping => {
                            if (mapping.clientAdGroups) {
                              allClientGroups.push(...mapping.clientAdGroups);
                            }
                            if (mapping.accentureAdGroups) {
                              allAccentureGroups.push(...mapping.accentureAdGroups);
                            }
                          });
                        }

                        return allClientGroups.length > 0 || allAccentureGroups.length > 0;
                      })() && (
                        <Box style={{ marginBottom: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                          <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 600, color: '#666' }}>
                            Available AD Groups from SSO Request:
                          </Typography>
                          <Grid container spacing={2}>
                            {(() => {
                              const fullData = typeof selectedRequestForApps.FullRequestData === 'string'
                                ? JSON.parse(selectedRequestForApps.FullRequestData)
                                : selectedRequestForApps.FullRequestData;

                              // Extract AD groups from roleMappings
                              const allClientGroups = [];
                              const allAccentureGroups = [];

                              if (fullData.accessControl && fullData.accessControl.roleMappings) {
                                fullData.accessControl.roleMappings.forEach(mapping => {
                                  if (mapping.clientAdGroups) {
                                    allClientGroups.push(...mapping.clientAdGroups);
                                  }
                                  if (mapping.accentureAdGroups) {
                                    allAccentureGroups.push(...mapping.accentureAdGroups);
                                  }
                                });
                              }

                              // Remove duplicates
                              const uniqueClientGroups = [...new Set(allClientGroups)];
                              const uniqueAccentureGroups = [...new Set(allAccentureGroups)];

                              return (
                                <>
                                  {/* Client AD Groups */}
                                  {uniqueClientGroups.length > 0 && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" style={{ color: '#6a1b9a', fontWeight: 'bold' }}>
                                        Client AD Groups:
                                      </Typography>
                                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                        {uniqueClientGroups.map((group, idx) => (
                                          <Chip
                                            key={`client-${idx}`}
                                            label={group}
                                            size="small"
                                            clickable
                                            onClick={() => handleAddExistingAdGroup(group)}
                                            style={{ backgroundColor: '#e1f5fe', color: '#0277bd', cursor: 'pointer', fontSize: '0.7rem' }}
                                            title="Click to add to application"
                                          />
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Accenture AD Groups */}
                                  {uniqueAccentureGroups.length > 0 && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" style={{ color: '#6a1b9a', fontWeight: 'bold' }}>
                                        Accenture AD Groups:
                                      </Typography>
                                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                        {uniqueAccentureGroups.map((group, idx) => (
                                          <Chip
                                            key={`accenture-${idx}`}
                                            label={group}
                                            size="small"
                                            clickable
                                            onClick={() => handleAddExistingAdGroup(group)}
                                            style={{ backgroundColor: '#fff3e0', color: '#f57c00', cursor: 'pointer', fontSize: '0.7rem' }}
                                            title="Click to add to application"
                                          />
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}
                                </>
                              );
                            })()}
                          </Grid>
                        </Box>
                      )}

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <StyledSelect
                              value={adGroupType}
                              onChange={(e) => setAdGroupType(e.target.value)}
                              variant="outlined"
                              size="small"
                              style={{ minWidth: 120 }}
                            >
                              <MenuItem value="client">Client Group</MenuItem>
                              <MenuItem value="accenture">Accenture Group</MenuItem>
                            </StyledSelect>
                            <TextField
                              label="Add New AD Group"
                              value={newAdGroup}
                              onChange={(e) => setNewAdGroup(e.target.value)}
                              variant="outlined"
                              size="small"
                              placeholder="e.g., MyWizard-Users, Admin-Group"
                              style={{ flex: 1 }}
                            />
                            <Button
                              onClick={handleAddAdGroup}
                              variant="contained"
                              size="small"
                              startIcon={<AddIcon />}
                              style={{ backgroundColor: '#6a1b9a', color: 'white' }}
                            >
                              Add
                            </Button>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          {appFormData.adGroups && Array.isArray(appFormData.adGroups) && appFormData.adGroups.length > 0 ? (
                            <Box>
                              <Typography variant="body2" style={{ marginBottom: 8, color: '#666' }}>
                                Selected AD Groups for this Application:
                              </Typography>
                              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {appFormData.adGroups.filter(group => group != null).map((group, index) => {
                                  // Handle both old format (strings) and new format (objects)
                                  let groupLabel, groupName;
                                  if (typeof group === 'string') {
                                    groupLabel = groupName = group;
                                  } else if (group && group.groups) {
                                    groupLabel = groupName = group.groups;
                                  } else {
                                    return null; // Skip invalid entries
                                  }

                                  const fullData = selectedRequestForApps && selectedRequestForApps.FullRequestData
                                    ? (typeof selectedRequestForApps.FullRequestData === 'string'
                                        ? JSON.parse(selectedRequestForApps.FullRequestData)
                                        : selectedRequestForApps.FullRequestData)
                                    : {};

                                  // Extract all client and accenture groups from roleMappings
                                  const allClientGroups = [];
                                  const allAccentureGroups = [];

                                  if (fullData.accessControl && fullData.accessControl.roleMappings) {
                                    fullData.accessControl.roleMappings.forEach(mapping => {
                                      if (mapping.clientAdGroups) {
                                        allClientGroups.push(...mapping.clientAdGroups);
                                      }
                                      if (mapping.accentureAdGroups) {
                                        allAccentureGroups.push(...mapping.accentureAdGroups);
                                      }
                                    });
                                  }

                                  // Determine color based on group type from the object or original SSO categorization
                                  let chipStyle = { backgroundColor: '#e8f5e8', color: '#2e7d32' }; // Default for custom groups

                                  if (typeof group === 'object' && group.adGroupType) {
                                    // Use the categorization from the object
                                    if (group.adGroupType === 'CLIENT') {
                                      chipStyle = { backgroundColor: '#e1f5fe', color: '#0277bd' };
                                    } else if (group.adGroupType === 'ACCENTURE') {
                                      chipStyle = { backgroundColor: '#fff3e0', color: '#f57c00' };
                                    }
                                  } else {
                                    // Fallback to SSO request categorization for old format
                                    const isClientGroup = allClientGroups.includes(groupName);
                                    const isAccentureGroup = allAccentureGroups.includes(groupName);

                                    if (isClientGroup) {
                                      chipStyle = { backgroundColor: '#e1f5fe', color: '#0277bd' };
                                    } else if (isAccentureGroup) {
                                      chipStyle = { backgroundColor: '#fff3e0', color: '#f57c00' };
                                    }
                                  }

                                  return (
                                    <Chip
                                      key={index}
                                      label={groupLabel}
                                      onDelete={() => handleRemoveAdGroup(index)}
                                      size="small"
                                      style={chipStyle}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic' }}>
                              No AD groups selected yet. Click on available groups above or add new ones.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}

              </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {showAppForm ? (
            <>
              <Button onClick={handleBackToList} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleSaveApplication}
                variant="contained"
                style={{ backgroundColor: '#6a1b9a', color: 'white' }}
                disabled={!appFormData.name.trim() || !appFormData.url.trim()}
              >
                {isEditMode ? 'Update Application' : 'Add Application'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseAppDialog} color="primary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Workflow Tracker Dialog */}
      <WorkflowTracker
        open={workflowDialogOpen}
        onClose={() => setWorkflowDialogOpen(false)}
        requestId={selectedRequestForWorkflow && selectedRequestForWorkflow.RequestID}
        requestData={selectedRequestForWorkflow}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        keepMounted
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{
          style: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 8,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => handleMenuAction('view-details')} style={{ padding: '8px 16px' }}>
          <Visibility fontSize="small" style={{ color: '#1976d2', marginRight: 12 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('manage-apps')} style={{ padding: '8px 16px' }}>
          <AppsIcon fontSize="small" style={{ color: '#ff9800', marginRight: 12 }} />
          Manage Applications
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('view-workflow')} style={{ padding: '8px 16px' }}>
          <TimelineIcon fontSize="small" style={{ color: '#4caf50', marginRight: 12 }} />
          View Workflow Progress
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default withStyles(styles)(SSORequestsList);